import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import { useTranslation } from "react-i18next";
import api from "../../services/api";

const MODES = ["child_alternative", "diet_alternative", "optional_second_recipe"];
const GROUPS = ["children", "adults", "one_person", "custom"];

const EMPTY_FORM = {
  mode: "child_alternative",
  target_group_label: "children",
  servings: "1",
  reason: "",
  notes: "",
};

function recipeLabel(recipe, fallback = "") {
  return String(recipe?.name || recipe?.title || recipe?.recipe_name || fallback || "").trim();
}

function recipeId(recipe) {
  return recipe?.id ?? recipe?.recipe_id ?? recipe?.alternative_recipe_id ?? null;
}

function normalizeRecipeResults(payload) {
  const body = payload?.data || payload || {};
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.recipes)) return body.recipes;
  if (Array.isArray(body.items)) return body.items;
  return [];
}

function normalizeOptions(payload) {
  const body = payload?.data || payload || {};
  const data = body.data || body;
  if (Array.isArray(data.options)) return data.options;
  if (Array.isArray(data.split_options)) return data.split_options;
  if (Array.isArray(body.options)) return body.options;
  return [];
}

function normalizePreview(payload) {
  const body = payload?.data || payload || {};
  return body.data || body.preview || body;
}

function normalizeSuggestions(payload) {
  const body = payload?.data || payload || {};
  return body.data || body;
}

function optionRecipeName(option, t) {
  return (
    option?.recipe_name ||
    option?.alternative_recipe_name ||
    option?.alternative_recipe?.name ||
    t("smart_kitchen.split_meals.unknown_recipe")
  );
}

function ingredientName(item, fallback) {
  return item?.name || item?.generic_product_name || item?.product_name || fallback;
}

function ingredientMeta(item, t) {
  const quantity = item?.quantity ?? item?.qty_needed_base ?? item?.total_quantity;
  const unit = item?.unit || item?.base_unit || "";
  return quantity !== undefined && quantity !== null && quantity !== ""
    ? t("smart_kitchen.split_meals.quantity_unit", { quantity, unit })
    : t("smart_kitchen.empty_value");
}

function formatMoney(value, currency = "EUR") {
  if (value === null || value === undefined || value === "") return "";
  const symbol = currency === "USD" ? "$" : "€";
  return `${symbol}${Number(value || 0).toFixed(2)}`;
}

function warningRecipeId(warning) {
  if (warning?.recipe_id || warning?.recipeId) return warning.recipe_id || warning.recipeId;
  const match = String(warning?.message || "").match(/recipe\s+(\d+)/i);
  return match?.[1] || "";
}

function warningText(warning, t) {
  if (warning?.code === "recipe_has_no_products") {
    return t("smart_kitchen.split_meals.warning_recipe_missing_mapping", {
      recipeId: warningRecipeId(warning) || t("smart_kitchen.empty_value"),
    });
  }
  return warning?.friendly_message || warning?.message || warning?.code || t("smart_kitchen.split_meals.warning_fallback");
}

function IngredientList({ title, items = [], emptyLabel, testId }) {
  const { t } = useTranslation();
  return (
    <Box data-testid={testId}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
        {title}
      </Typography>
      {items.length ? (
        <List dense disablePadding>
          {items.map((item, index) => (
            <ListItem key={`${ingredientName(item, "item")}-${index}`} disableGutters>
              <ListItemText
                primary={ingredientName(item, t("smart_kitchen.split_meals.unknown_item"))}
                secondary={ingredientMeta(item, t)}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      )}
    </Box>
  );
}

export default function SplitMealsManageDialog({ open, meal, onClose }) {
  const { t } = useTranslation();
  const itemId = meal?.meal_plan_item_id;
  const primaryRecipeName = meal?.recipe_name || t("smart_kitchen.split_meals.unknown_recipe");

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeQuery, setRecipeQuery] = useState("");
  const [recipeOptions, setRecipeOptions] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipeSearched, setRecipeSearched] = useState(false);
  const [recipeError, setRecipeError] = useState("");

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingOptionId(null);
    setSelectedRecipe(null);
    setRecipeQuery("");
    setRecipeOptions([]);
    setRecipeSearched(false);
    setRecipeError("");
  }, []);

  const loadOptions = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError("");
    setPreview(null);
    try {
      const response = await api.smartKitchen.getSplitOptions(itemId);
      setOptions(normalizeOptions(response));
    } catch (err) {
      setError(err?.response?.data?.message || t("smart_kitchen.split_meals.manage_load_error"));
    } finally {
      setLoading(false);
    }
  }, [itemId, t]);

  const loadSuggestions = useCallback(async () => {
    if (!itemId) return;
    setSuggestionsLoading(true);
    try {
      const response = await api.smartKitchen.getSplitOptionSuggestions(itemId);
      setSuggestions(normalizeSuggestions(response));
    } catch (_err) {
      setSuggestions(null);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setOptions([]);
    setPreview(null);
    setSuggestions(null);
    setError("");
    setSuccess("");
    loadOptions();
    loadSuggestions();
  }, [loadOptions, loadSuggestions, open, resetForm]);

  const searchRecipes = useCallback(async (query) => {
    const safeQuery = String(query || "").trim();
    if (safeQuery.length < 2) {
      setRecipeOptions([]);
      setRecipeSearched(false);
      return;
    }
    setRecipeLoading(true);
    setRecipeError("");
    setRecipeSearched(true);
    try {
      const response = await api.recipes.findByName(safeQuery);
      setRecipeOptions(normalizeRecipeResults(response));
    } catch (err) {
      setRecipeOptions([]);
      setRecipeError(err?.response?.data?.message || err?.message || t("smart_kitchen.split_meals.recipe_search_error"));
    } finally {
      setRecipeLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open) return undefined;
    const safeQuery = recipeQuery.trim();
    if (safeQuery.length < 2) {
      setRecipeOptions([]);
      setRecipeSearched(false);
      return undefined;
    }
    const timer = setTimeout(() => searchRecipes(safeQuery), 300);
    return () => clearTimeout(timer);
  }, [open, recipeQuery, searchRecipes]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const buildAlternative = () => {
    const alternativeRecipeId = recipeId(selectedRecipe);
    const servings = Number(form.servings);
    return {
      id: editingOptionId || undefined,
      mode: form.mode,
      alternative_recipe_id: alternativeRecipeId,
      recipe_id: alternativeRecipeId,
      recipe_name: recipeLabel(selectedRecipe),
      target_group_label: form.target_group_label,
      servings,
      reason: form.reason,
      notes: form.notes,
    };
  };

  const validate = () => {
    if (!MODES.includes(form.mode)) return t("smart_kitchen.split_meals.validation_mode_required");
    if (!recipeId(selectedRecipe)) return t("smart_kitchen.split_meals.validation_recipe_required");
    if (!(Number(form.servings) > 0)) return t("smart_kitchen.split_meals.validation_servings_positive");
    return "";
  };

  const saveOption = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const alternative = buildAlternative();
    const nextOptions = [
      ...options.filter((option) => String(option.id) !== String(editingOptionId)),
      alternative,
    ].map((option, index) => ({
      ...option,
      sort_order: index,
    }));

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.smartKitchen.saveSplitOptions(itemId, {
        mode: form.mode,
        alternatives: nextOptions,
      });
      setOptions(normalizeOptions(response));
      setPreview(null);
      setSuccess(t("smart_kitchen.split_meals.manage_saved_success"));
      resetForm();
    } catch (err) {
      setError(err?.response?.data?.message || t("smart_kitchen.split_meals.manage_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const editOption = (option) => {
    setEditingOptionId(option.id || null);
    setForm({
      mode: option.mode || "child_alternative",
      target_group_label: option.target_group_label || "children",
      servings: String(option.servings || 1),
      reason: option.reason || "",
      notes: option.notes || "",
    });
    const recipe = {
      id: option.alternative_recipe_id || option.recipe_id,
      name: optionRecipeName(option, t),
    };
    setSelectedRecipe(recipe);
    setRecipeQuery(recipe.name);
    setRecipeOptions([]);
    setRecipeError("");
  };

  const deleteOption = async (option) => {
    if (!option?.id) return;
    setDeletingId(option.id);
    setError("");
    setSuccess("");
    try {
      const response = await api.smartKitchen.deleteSplitOption(itemId, option.id);
      setOptions(normalizeOptions(response));
      setPreview(null);
      setSuccess(t("smart_kitchen.split_meals.manage_deleted_success"));
      if (String(editingOptionId) === String(option.id)) resetForm();
    } catch (err) {
      setError(err?.response?.data?.message || t("smart_kitchen.split_meals.manage_delete_error"));
    } finally {
      setDeletingId(null);
    }
  };

  const previewSaved = async () => {
    setPreviewLoading(true);
    setError("");
    try {
      const response = await api.smartKitchen.previewSavedSplitOptions(itemId);
      setPreview(normalizePreview(response));
    } catch (err) {
      setError(err?.response?.data?.message || t("smart_kitchen.split_meals.manage_preview_error"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const applySuggestion = (recipe) => {
    const nextMode = suggestions?.suggested_mode || form.mode || "child_alternative";
    const servings = suggestions?.target_member_count || 1;
    setForm((current) => ({
      ...current,
      mode: nextMode,
      target_group_label: suggestions?.target_group_value || current.target_group_label || "custom",
      servings: String(servings),
      reason: suggestions?.reason_key || current.reason,
    }));
    setSelectedRecipe({
      id: recipe.recipe_id,
      recipe_id: recipe.recipe_id,
      name: recipe.recipe_name,
      recipe_name: recipe.recipe_name,
    });
    setRecipeQuery(recipe.recipe_name || "");
    setRecipeOptions([]);
    setRecipeError("");
    setSuccess("");
    setError("");
  };

  const totalServingsRows = useMemo(() => Object.entries(preview?.total_servings_by_recipe || {}), [preview]);
  const shoppingImpact = preview?.shopping_impact || {};
  const currency = preview?.currency || "EUR";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" data-testid="split-meals-manage-dialog">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <RestaurantMenuRoundedIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {t("smart_kitchen.split_meals.manage_title")}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.25}>
          <Alert severity="info">
            <Stack spacing={0.5}>
              <Typography variant="body2">{t("smart_kitchen.split_meals.primary_stays")}</Typography>
              <Typography variant="body2">{t("smart_kitchen.split_meals.optional_alternatives")}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>{t("smart_kitchen.split_meals.no_regenerate")}</Typography>
            </Stack>
          </Alert>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("smart_kitchen.split_meals.primary_readonly")}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 900 }}>
              {primaryRecipeName}
            </Typography>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  {t("smart_kitchen.split_meals.saved_options")}
                </Typography>
                {loading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">{t("smart_kitchen.split_meals.loading_options")}</Typography>
                  </Stack>
                ) : options.length ? (
                  <List dense disablePadding data-testid="split-meals-saved-options">
                    {options.map((option) => (
                      <ListItem
                        key={option.id || `${option.alternative_recipe_id}-${option.sort_order}`}
                        disableGutters
                        secondaryAction={
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => editOption(option)} aria-label={t("smart_kitchen.split_meals.edit_option")}>
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => deleteOption(option)}
                              disabled={deletingId === option.id}
                              aria-label={t("smart_kitchen.split_meals.delete_option")}
                              data-testid={`split-meals-delete-option-${option.id}`}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={optionRecipeName(option, t)}
                          secondary={`${t(`smart_kitchen.split_meals.modes.${option.mode || "child_alternative"}`)} · ${t("smart_kitchen.split_meals.recipe_servings", {
                            recipeId: option.alternative_recipe_id || option.recipe_id || t("smart_kitchen.empty_value"),
                            servings: option.servings || 1,
                          })}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("smart_kitchen.split_meals.no_saved_options")}
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  startIcon={<PreviewRoundedIcon />}
                  onClick={previewSaved}
                  disabled={previewLoading || !options.length}
                  data-testid="split-meals-preview-saved-options"
                >
                  {previewLoading ? t("smart_kitchen.split_meals.previewing") : t("smart_kitchen.split_meals.preview_saved")}
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={7}>
              <Stack spacing={1.5}>
                {suggestionsLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">{t("smart_kitchen.split_meals.loading_suggestions")}</Typography>
                  </Stack>
                ) : suggestions?.needs_split_meal ? (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }} data-testid="split-meals-suggestions">
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        {t("smart_kitchen.split_meals.suggested_alternative")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("smart_kitchen.split_meals.suggestion_body", {
                          group: suggestions.target_group_label || t("smart_kitchen.empty_value"),
                        })}
                      </Typography>
                      {(suggestions.suggested_recipes || []).length ? (
                        <Stack spacing={1}>
                          {suggestions.suggested_recipes.map((recipe) => (
                            <Stack
                              key={recipe.recipe_id}
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "stretch", sm: "center" }}
                              justifyContent="space-between"
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {recipe.recipe_name || t("smart_kitchen.split_meals.unknown_recipe")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t("smart_kitchen.split_meals.recipe_result_meta", { id: recipe.recipe_id })}
                                </Typography>
                              </Box>
                              <Button size="small" variant="outlined" onClick={() => applySuggestion(recipe)}>
                                {t("smart_kitchen.split_meals.use_suggestion")}
                              </Button>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("smart_kitchen.split_meals.no_suggestions_guidance")}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                ) : null}

                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  {editingOptionId ? t("smart_kitchen.split_meals.edit_alternative") : t("smart_kitchen.split_meals.add_alternative")}
                </Typography>
                <TextField select fullWidth label={t("smart_kitchen.split_meals.mode")} value={form.mode} onChange={updateField("mode")} inputProps={{ "data-testid": "split-meals-manage-mode" }}>
                  {MODES.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {t(`smart_kitchen.split_meals.modes.${mode}`)}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  value={selectedRecipe}
                  inputValue={recipeQuery}
                  options={recipeOptions}
                  loading={recipeLoading}
                  getOptionLabel={(option) => recipeLabel(option, t("smart_kitchen.split_meals.unknown_recipe"))}
                  isOptionEqualToValue={(option, value) => String(recipeId(option)) === String(recipeId(value))}
                  onInputChange={(_event, value, reason) => {
                    if (reason !== "reset") setRecipeQuery(value);
                  }}
                  onChange={(_event, value) => {
                    setSelectedRecipe(value);
                    setRecipeQuery(recipeLabel(value));
                  }}
                  noOptionsText={
                    recipeError
                      ? t("smart_kitchen.split_meals.recipe_search_error")
                      : recipeSearched
                        ? t("smart_kitchen.split_meals.no_recipe_results")
                        : t("smart_kitchen.split_meals.recipe_search_empty")
                  }
                  loadingText={t("smart_kitchen.split_meals.recipe_search_loading")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={t("smart_kitchen.split_meals.alternative_recipe")}
                      placeholder={t("smart_kitchen.split_meals.recipe_search_placeholder")}
                      inputProps={{
                        ...params.inputProps,
                        "data-testid": "split-meals-manage-recipe-search",
                      }}
                    />
                  )}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => searchRecipes(recipeQuery)}
                  disabled={recipeLoading || recipeQuery.trim().length < 2}
                  data-testid="split-meals-manage-recipe-search-button"
                >
                  {recipeLoading ? t("smart_kitchen.split_meals.recipe_search_loading") : t("smart_kitchen.split_meals.recipe_search_button")}
                </Button>
                {selectedRecipe ? (
                  <Chip
                    color="primary"
                    variant="outlined"
                    label={t("smart_kitchen.split_meals.selected_recipe", { name: recipeLabel(selectedRecipe) })}
                    onDelete={() => {
                      setSelectedRecipe(null);
                      setRecipeQuery("");
                    }}
                    data-testid="split-meals-manage-selected-recipe"
                  />
                ) : null}
                {recipeError ? <Alert severity="warning">{recipeError}</Alert> : null}

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t("smart_kitchen.split_meals.servings")} value={form.servings} onChange={updateField("servings")} inputProps={{ inputMode: "numeric", "data-testid": "split-meals-manage-servings" }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth label={t("smart_kitchen.split_meals.target_group")} value={form.target_group_label} onChange={updateField("target_group_label")}>
                      {GROUPS.map((group) => (
                        <MenuItem key={group} value={group}>
                          {t(`smart_kitchen.split_meals.groups.${group}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label={t("smart_kitchen.split_meals.reason_optional")} value={form.reason} onChange={updateField("reason")} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={2} label={t("smart_kitchen.split_meals.notes_optional")} value={form.notes} onChange={updateField("notes")} />
                  </Grid>
                </Grid>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="contained" onClick={saveOption} disabled={saving || !itemId} data-testid="split-meals-save-option">
                    {saving
                      ? t("smart_kitchen.split_meals.saving_option")
                      : editingOptionId
                        ? t("smart_kitchen.split_meals.update_option")
                        : t("smart_kitchen.split_meals.save_option")}
                  </Button>
                  <Button variant="text" onClick={resetForm}>
                    {t("smart_kitchen.split_meals.clear_form")}
                  </Button>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          {preview ? (
            <Box data-testid="split-meals-saved-preview">
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {t("smart_kitchen.split_meals.preview_title")}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        {t("smart_kitchen.split_meals.cost_summary")}
                      </Typography>
                      <Typography variant="body2">
                        {t("smart_kitchen.split_meals.total_cost")}: {formatMoney(preview.estimated_total_cost, currency) || t("smart_kitchen.empty_value")}
                      </Typography>
                      <Typography variant="body2">
                        {t("smart_kitchen.split_meals.extra_cost")}: {formatMoney(preview.estimated_extra_cost, currency) || t("smart_kitchen.empty_value")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        {t("smart_kitchen.split_meals.shopping_impact")}
                      </Typography>
                      <Typography variant="body2">{t("smart_kitchen.split_meals.shared_count", { count: shoppingImpact.shared_ingredients ?? preview.shared_ingredient_count ?? 0 })}</Typography>
                      <Typography variant="body2">{t("smart_kitchen.split_meals.extra_count", { count: shoppingImpact.extra_ingredients ?? preview.extra_ingredient_count ?? 0 })}</Typography>
                      <Typography variant="body2">{t("smart_kitchen.split_meals.additional_products_count", { count: shoppingImpact.additional_products_needed ?? 0 })}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        {t("smart_kitchen.split_meals.total_servings")}
                      </Typography>
                      {totalServingsRows.length ? totalServingsRows.map(([recipe, servings]) => (
                        <Typography key={recipe} variant="body2">
                          {t("smart_kitchen.split_meals.recipe_servings", { recipeId: recipe, servings })}
                        </Typography>
                      )) : (
                        <Typography variant="body2" color="text.secondary">{t("smart_kitchen.empty_value")}</Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <IngredientList title={t("smart_kitchen.split_meals.combined_items")} items={preview.combined_items || []} emptyLabel={t("smart_kitchen.split_meals.no_combined_items")} testId="split-meals-saved-combined-items" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <IngredientList title={t("smart_kitchen.split_meals.shared_ingredients")} items={preview.shared_ingredients || []} emptyLabel={t("smart_kitchen.split_meals.no_shared_items")} testId="split-meals-saved-shared-items" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <IngredientList title={t("smart_kitchen.split_meals.extra_ingredients")} items={preview.extra_ingredients || []} emptyLabel={t("smart_kitchen.split_meals.no_extra_items")} testId="split-meals-saved-extra-items" />
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                    {t("smart_kitchen.split_meals.warnings")}
                  </Typography>
                  {(preview.warnings || []).length ? (
                    <Stack spacing={1}>
                      {preview.warnings.map((warning, index) => (
                        <Alert key={`${warning?.code || "warning"}-${index}`} severity="warning">
                          {warningText(warning, t)}
                        </Alert>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t("smart_kitchen.split_meals.no_warnings")}</Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
